import { useState, useCallback } from 'react';
import { BlockedSignupLog } from '@/types';

// 타입 정의
export interface SearchState {
  pending: string;
  allUsers: string;
}

export interface BlockedState {
  logs: BlockedSignupLog[];
  loading: boolean;
  includeDeleted: boolean;
  confirmOpen: boolean;
}

export interface TabCounts {
  pending: number;
  allUsers: number;
  blocked: number;
}

export interface CustomerItem {
  id: number;
  customer_name: string;
  customer_code: string;
}

export interface UserManagementStateReturn {
  // 검색어 상태
  search: SearchState;
  setSearch: (partial: Partial<SearchState>) => void;

  // 차단목록 상태
  blocked: BlockedState;
  setBlocked: (partial: Partial<BlockedState>) => void;

  // 탭 카운트 상태
  counts: TabCounts;
  setCounts: (partial: Partial<TabCounts>) => void;

  // 새로고침 상태
  refresh: {
    key: number;
    trigger: () => void;
  };

  // 고객사 목록 상태
  customers: {
    list: CustomerItem[];
    setList: (customers: CustomerItem[]) => void;
  };
}

/**
 * 사용자 관리 공통 상태 관리 hook
 * admin/index.tsx와 superadmin/index.tsx에서 공통으로 사용되는 상태를 관리합니다.
 *
 * 구조:
 * - search: 검색어 상태 { pending, allUsers }
 * - blocked: 차단목록 상태 { logs, loading, includeDeleted, confirmOpen }
 * - counts: 탭 카운트 상태 { pending, allUsers, blocked }
 * - refresh: 새로고침 상태 { key, trigger }
 * - customers: 고객사 목록 상태 { list, setList }
 */
export const useUserManagementState = (): UserManagementStateReturn => {
  // 검색어 상태
  const [searchState, setSearchState] = useState<SearchState>({
    pending: '',
    allUsers: '',
  });

  // 차단목록 상태
  const [blockedState, setBlockedState] = useState<BlockedState>({
    logs: [],
    loading: false,
    includeDeleted: false,
    confirmOpen: false,
  });

  // 탭 카운트 상태
  const [countsState, setCountsState] = useState<TabCounts>({
    pending: 0,
    allUsers: 0,
    blocked: 0,
  });

  // 새로고침 상태
  const [refreshKey, setRefreshKey] = useState(0);

  // 고객사 목록 상태
  const [customersList, setCustomersList] = useState<CustomerItem[]>([]);

  // 부분 업데이트 헬퍼 함수들
  const setSearch = useCallback((partial: Partial<SearchState>) => {
    setSearchState(prev => ({ ...prev, ...partial }));
  }, []);

  const setBlocked = useCallback((partial: Partial<BlockedState>) => {
    setBlockedState(prev => ({ ...prev, ...partial }));
  }, []);

  const setCounts = useCallback((partial: Partial<TabCounts>) => {
    setCountsState(prev => ({ ...prev, ...partial }));
  }, []);

  const triggerRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  return {
    search: searchState,
    setSearch,

    blocked: blockedState,
    setBlocked,

    counts: countsState,
    setCounts,

    refresh: {
      key: refreshKey,
      trigger: triggerRefresh,
    },

    customers: {
      list: customersList,
      setList: setCustomersList,
    },
  };
};
